import React from 'react';

/**
 * This hook restores the horizontal scroll position of an `targetElementRef` once the `containerRef`
 * of `targetElementRef` is loaded so the element is initially visible
 */
export default function useScrollIntoViewHorizontal({
    containerRef,
    forceRerun,
    onItemClick,
    targetElementRef,
}: {
    containerRef: React.RefObject<HTMLDivElement | null>;
    forceRerun?: boolean;
    onItemClick?: () => void;
    targetElementRef: React.RefObject<HTMLDivElement | null>;
}) {
    const visibilityHandler = React.useCallback(() => {
        //Reset state when page visibility is hidden. Works with app linkout on phones
        if (document.visibilityState === 'hidden' && targetElementRef.current && containerRef.current) {
            initializeScrollPosition(containerRef.current, targetElementRef.current);
            onItemClick?.();
        }
    }, [targetElementRef, onItemClick, containerRef]);

    React.useEffect(() => {
        document.addEventListener('visibilitychange', visibilityHandler);

        if (!targetElementRef.current) {
            return;
        }

        const targetElement = targetElementRef.current;
        const resizeObserver = new ResizeObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.target || !containerRef.current) {
                    return;
                }
                initializeScrollPosition(containerRef.current, targetElement);
            });
        });

        if (containerRef?.current) {
            resizeObserver.observe(targetElement);
        }

        return () => {
            resizeObserver.unobserve(targetElement);
            document.removeEventListener('visibilitychange', visibilityHandler);
        };
    }, [targetElementRef, containerRef, visibilityHandler, forceRerun]);
}

const initializeScrollPosition = (containerElement: HTMLDivElement, targetElement: HTMLDivElement) => {
    const rect = targetElement.getBoundingClientRect();
    const scroll = rect.left + rect.width + containerElement.scrollLeft - containerElement.clientWidth;
    containerElement.scrollTo({ left: scroll + 11 });
};
